import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import type { ColumnDef } from '@tanstack/react-table';
import type { CustomerReferralRow } from '../../mapCustomerReferralReports';

const textCell = (value: string | number) => (
  <div className="whitespace-nowrap text-xs font-medium text-foreground-detail">{value}</div>
);

export const getIndividualCustomerColumns = (t: (key: string) => string): ColumnDef<CustomerReferralRow>[] => [
  {
    accessorKey: 'sn',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
    cell: ({ row, table }) => textCell(getSerialNumber(table, row.index)),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'reservedBy',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.reservedBy')} />,
    cell: ({ row }) => {
      const name = row.getValue<string>('reservedBy');
      const nickname = row.original.reservedByNickname;
      const hasName = name && name !== '-';
      const hasNickname = nickname && nickname !== '-';

      if (!hasName && !hasNickname) {
        return textCell('-');
      }

      const primary = hasName ? name : nickname;
      const secondary = hasName && hasNickname ? nickname : null;

      return (
        <div className="max-w-[15rem]">
          <div className="truncate whitespace-nowrap text-xs font-medium text-foreground-detail">{primary}</div>
          {secondary && (
            <div className="truncate whitespace-nowrap text-xs font-medium text-foreground-subtle">{secondary}</div>
          )}
        </div>
      );
    },
    enableHiding: false,
  },
  {
    accessorKey: 'reservationDateTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.reservationDateTime')} />
    ),
    cell: ({ row }) => textCell(formatDate(row.getValue<number>('reservationDateTime'), 'YYYY/MM/DD HH:mm:ss')),
    enableHiding: false,
  },
  {
    accessorKey: 'stageDetails',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.stageDetails')} />,
    cell: ({ row }) => (
      <div className="whitespace-nowrap text-xs font-medium text-success">{row.getValue<string>('stageDetails')}</div>
    ),
    enableHiding: false,
  },
  {
    accessorKey: 'pointsEarned',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.pointsEarned')} />,
    cell: ({ row }) => {
      const points = row.getValue<number | null>('pointsEarned');
      if (points === null || points === 0) {
        return <div>-</div>;
      }
      return <div className="whitespace-nowrap text-xs font-medium text-success">+{formatAmount(points, ' ')}</div>;
    },
    enableHiding: false,
  },
];
